/**
 * EditDeltaCalculator - Utility for calculating edit deltas from text changes
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility calculates edit deltas (position, deleteCount, insertText) from
 * old and new text, which is used for incremental text updates in WASM.
 */

/**
 * Result of edit delta calculation
 */
export interface EditDelta {
  position: number;
  deleteCount: number;
  insertText: string;
}

/**
 * Calculate edit delta from text change
 * 
 * Given old text, new text, and change boundaries, calculates:
 * - position: Where the edit starts (in old text coordinates)
 * - deleteCount: How many characters to delete from old text
 * - insertText: What text to insert
 * 
 * ⚠️ IMPORTANT: changeStart and changeEnd have different meanings:
 * - For INSERTIONS: changeStart is position in old text, changeEnd = changeStart
 * - For DELETIONS: changeStart is position in new text, changeEnd is position in old text
 * 
 * @param oldText - Original text before change
 * @param newText - New text after change
 * @param changeStart - Start position (in new text for deletions, old text for insertions)
 * @param changeEnd - End position (in old text for deletions, same as changeStart for insertions)
 * @returns Edit delta with position, deleteCount, and insertText
 */
export function calculateEditDelta(
  oldText: string,
  newText: string,
  changeStart: number,
  changeEnd: number
): EditDelta {
  // Calculate the length difference between old and new text
  const lengthDiff = newText.length - oldText.length;
  
  let position: number;
  let deleteCount: number;
  let insertText: string;
  
  if (lengthDiff < 0) {
    // ✅ DELETION: changeStart is in new text, changeEnd is in old text
    // Example: oldText="abc", newText="ac", changeStart=1 (new text), changeEnd=2 (old text)
    // We deleted 1 char starting at position 1 in old text
    // changeEnd represents the end of deleted region in old text
    deleteCount = -lengthDiff; // Number of chars deleted (lengthDiff is negative)
    position = changeEnd - deleteCount; // Position in old text where deletion starts
    insertText = ''; // No insertion
  } else if (lengthDiff > 0) {
    // ✅ INSERTION: changeStart is in old text, changeEnd = changeStart
    // Example: oldText="ac", newText="abc", changeStart=1, changeEnd=1
    // We inserted 1 char at position 1 in old text
    position = changeStart; // Position in old text where insertion happens
    deleteCount = 0; // No deletion
    insertText = newText.slice(changeStart, changeStart + lengthDiff); // Text to insert
  } else {
    // ✅ REPLACEMENT (same length): changeStart is in old text, changeEnd is end in old text
    // Example: oldText="abc", newText="adc", changeStart=1, changeEnd=2
    // We replaced 1 char at position 1
    position = changeStart; // Position in old text where replacement starts
    const replacementLength = changeEnd - changeStart; // Length of replaced region
    deleteCount = replacementLength; // Number of chars to delete from old text
    insertText = newText.slice(changeStart, changeStart + replacementLength); // Replacement text from new text
  }
  
  return {
    position,
    deleteCount,
    insertText
  };
}

